import { useState } from 'react';
import { askTutor, evaluateAnswer } from '../lib/ai';
import type { AiTutorGuidedAction, AiTutorRequest, AiTutorResponse, ModuleId } from '../lib/types';

interface AiTutorButtonProps {
  module: ModuleId;
  context: string;
  buttonText: string;
  feedbackPlaceholder?: string;
}

type TutorActionId = 'explain' | 'practice' | 'feedback';

interface ActionPreset {
  id: TutorActionId;
  task: AiTutorRequest['task'];
  label: string;
  description: string;
  prompt: string;
  needsUserText?: boolean;
  placeholder?: string;
}

const actionPresets: ActionPreset[] = [
  {
    id: 'explain',
    task: 'explain',
    label: 'توضیح ساده',
    description: 'موضوع درس را مرحله‌به‌مرحله توضیح بده',
    prompt: 'Explain this lesson simply in Persian with one practical example.',
  },
  {
    id: 'practice',
    task: 'generate',
    label: 'تمرین هدفمند',
    description: 'یک تمرین کوتاه و قابل انجام بساز',
    prompt: 'Create one short practice task tailored to this lesson.',
  },
  {
    id: 'feedback',
    task: 'feedback',
    label: 'بازخورد شخصی',
    description: 'روی پاسخ من بازخورد دقیق بده',
    prompt: 'Review the student answer, correct mistakes gently, and suggest one improvement.',
    needsUserText: true,
    placeholder: 'پاسخ یا تمرین خودتان را اینجا بنویسید...',
  },
];

function inferTask(actionId?: string, explicitTask?: AiTutorRequest['task']): AiTutorRequest['task'] {
  if (explicitTask) return explicitTask;
  const id = actionId?.toLowerCase() ?? '';
  if (id.includes('feedback')) return 'feedback';
  if (id.includes('practice') || id.includes('generate')) return 'generate';
  return 'explain';
}

function actionLabel(action: AiTutorGuidedAction, fallback: string): string {
  return action.title ?? action.label ?? fallback;
}

function needsUserInput(action: {
  id?: string;
  task?: AiTutorRequest['task'];
  needsUserText?: boolean;
}): boolean {
  if (action.needsUserText) return true;
  const task = inferTask(action.id, action.task);
  return task === 'feedback';
}

export default function AiTutorButton({
  module,
  context,
  buttonText,
  feedbackPlaceholder,
}: AiTutorButtonProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedAction, setSelectedAction] = useState<TutorActionId>('explain');
  const [userInput, setUserInput] = useState('');
  const [response, setResponse] = useState<AiTutorResponse | null>(null);

  const activePreset = actionPresets.find((preset) => preset.id === selectedAction) ?? actionPresets[0];

  async function runAction(action: {
    id?: string;
    task?: AiTutorRequest['task'];
    prompt?: string;
    description?: string;
    needsUserText?: boolean;
  }) {
    setLoading(true);
    setResponse(null);

    try {
      const task = inferTask(action.id, action.task);
      const trimmedUserInput = userInput.trim();
      const shouldSendUserText = needsUserInput(action) && trimmedUserInput.length > 0;
      const result = await askTutor({
        module,
        task,
        context: [context, action.prompt ? `Action: ${action.prompt}` : '', action.description ? `Goal: ${action.description}` : '']
          .filter(Boolean)
          .join('\n'),
        userText: shouldSendUserText ? trimmedUserInput : undefined,
      });

      if (task === 'feedback' && trimmedUserInput.length > 0) {
        try {
          const evaluation = await evaluateAnswer({
            module,
            prompt: 'Please evaluate this learner response for this lesson context.',
            answer: trimmedUserInput,
            context,
            strictness: 'balanced',
          });

          setResponse({
            ...result,
            explanation: evaluation.feedback,
            exercise: evaluation.next_step,
            suggestions: [
              ...evaluation.strengths.slice(0, 2),
              ...evaluation.issues.slice(0, 2),
            ],
            feedbackScore: evaluation.score,
            feedbackVerdict: evaluation.verdict,
          });
          return;
        } catch {
          // Keep tutor fallback response when evaluation endpoint is unavailable.
        }
      }

      setResponse(result);
    } catch {
      setResponse({
        explanation: 'خطایی رخ داد. لطفاً دوباره تلاش کنید.',
      });
    } finally {
      setLoading(false);
    }
  }

  function handleClose() {
    setOpen(false);
    setResponse(null);
    setLoading(false);
    setSelectedAction('explain');
    setUserInput('');
  }

  return (
    <>
      <button className="ai-tutor-trigger" onClick={() => setOpen(true)}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
        {buttonText}
      </button>

      {open && (
        <div className="ai-tutor-overlay" onClick={handleClose}>
          <div className="ai-tutor-panel" onClick={(e) => e.stopPropagation()}>
            <div className="ai-tutor-header">
              <h3>دستیار هوشمند</h3>
              <button className="ai-tutor-close" onClick={handleClose} aria-label="بستن">
                &times;
              </button>
            </div>

            <p className="ai-tutor-guide-label">ابتدا نوع کمک را انتخاب کنید</p>
            <div className="ai-tutor-actions">
              {actionPresets.map((action) => (
                <button
                  key={action.id}
                  className={`ai-action-btn ${selectedAction === action.id ? 'ai-action-btn-active' : ''}`}
                  onClick={() => setSelectedAction(action.id)}
                  disabled={loading}
                >
                  <span className="ai-action-title">{action.label}</span>
                  <span className="ai-action-desc">{action.description}</span>
                </button>
              ))}
            </div>

            {activePreset.needsUserText && (
              <div className="ai-feedback-wrap">
                <p className="ai-feedback-note">این بخش اختیاری است اما برای بازخورد دقیق‌تر کمک می‌کند.</p>
                <textarea
                  className="ai-feedback-input"
                  rows={4}
                  placeholder={response?.feedbackPrompt ?? feedbackPlaceholder ?? activePreset.placeholder}
                  value={userInput}
                  onChange={(e) => setUserInput(e.target.value)}
                  disabled={loading}
                />
              </div>
            )}

            <button
              className="btn-primary ai-run-btn"
              onClick={() => runAction(activePreset)}
              disabled={loading}
            >
              {loading ? 'در حال آماده‌سازی پاسخ...' : 'دریافت راهنمایی'}
            </button>

            <div className="ai-tutor-body">
              {loading && (
                <div className="ai-loading">
                  <span className="dot" />
                  <span className="dot" />
                  <span className="dot" />
                </div>
              )}

              {response && (
                <div className="ai-bubble">
                  {typeof response.feedbackScore === 'number' && (
                    <p className="ai-section">
                      <strong>امتیاز بازخورد:</strong> {response.feedbackScore}
                      {response.feedbackVerdict ? ` (${response.feedbackVerdict})` : ''}
                    </p>
                  )}

                  <p className="ai-section">{response.explanation}</p>

                  {response.exercise && (
                    <p className="ai-section">
                      <strong>تمرین پیشنهادی:</strong> {response.exercise}
                    </p>
                  )}

                  {response.suggestions && response.suggestions.length > 0 && (
                    <ul className="ai-suggestion-list">
                      {response.suggestions.map((suggestion) => (
                        <li key={suggestion}>{suggestion}</li>
                      ))}
                    </ul>
                  )}

                  {response.followUpPrompt && (
                    <p className="ai-followup-text">{response.followUpPrompt}</p>
                  )}

                  {response.encouragement && (
                    <p className="ai-encouragement-text">{response.encouragement}</p>
                  )}
                </div>
              )}

              {response?.guidedActions && response.guidedActions.length > 0 && (
                <div className="ai-followup-actions">
                  <p className="ai-followup-title">اقدام بعدی پیشنهادی</p>
                  <div className="ai-followup-list">
                    {response.guidedActions.map((action, idx) => (
                      <button
                        key={`${action.id ?? idx}-${action.title ?? action.label ?? idx}`}
                        className="ai-followup-btn"
                        onClick={() =>
                          runAction({
                            id: action.id,
                            task: action.task,
                            prompt: action.prompt,
                            description: action.description,
                            needsUserText: action.needsUserText,
                          })
                        }
                        disabled={loading}
                      >
                        {actionLabel(action, `اقدام ${idx + 1}`)}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
